/**
 * Axios 請求配置模組
 * 
 * 自動在請求頭中添加 Token
 * 處理請求和響應攔截
 */

import axios from 'axios';
import { CONFIG } from '../../config';
import { generateFullToken, isLoggedIn } from './token';
import { logout } from './auth';

/**
 * 不需要 Token 的 API 白名單
 * 這些 API 可以在未登錄狀態下調用
 */
const TOKEN_WHITELIST = [
    // 登錄相關 API
    '/EmpLogin',
    '/EmpLoginWithVerificationCode',
    '/SendVerificationCode',
    '/ForgotPassword',
    '/EmpResetPassword',
    '/EmpForgotPassword',
];

/**
 * 檢查請求路徑是否在白名單中
 * @param {string} url - 請求路徑
 * @returns {boolean} 是否在白名單中
 */
function isWhitelistedPath(url) {
    if (!url) return false;

    return TOKEN_WHITELIST.some(path => {
        // 支援完全匹配和前綴匹配
        return url.includes(path);
    });
}

// 創建 axios 實例
const service = axios.create({
    baseURL: CONFIG.API_URL,
    timeout: 15000, // 請求超時時間 15 秒
    headers: {
        'Content-Type': 'application/json',
    }
});

// 請求攔截器
service.interceptors.request.use(
    config => {
        // 添加 lang 參數到 query，優先從 localStorage 獲取，預設為 en-US
        const langSetting = localStorage.getItem('languageSetting') || 'en-US';
        if (!config.params) {
            config.params = {};
        }
        // 支援 URLSearchParams 或 Plain Object
        if (typeof config.params.append === 'function') {
            if (!config.params.has('lang')) {
                config.params.append('lang', langSetting);
            }
        } else {
            config.params.lang = langSetting;
        }

        const requestUrl = config.url || '';
        // 檢查是否在白名單中
        const isWhitelisted = isWhitelistedPath(requestUrl);

        if (isWhitelisted) {
            // 白名單路徑，不需要 Token
            console.log('⚪ 白名單請求，無需 Token:', requestUrl);
        } else {
            // 需要 Token 的請求
            if (!isLoggedIn()) {
                console.warn('⚠️ 未登錄，即將跳轉到登錄頁');
                // 可以在這裡觸發跳轉到登錄頁
                // window.location.href = '/login';
                // 或者拋出錯誤
                return Promise.reject(new Error('未登錄，請先登錄'));
            }

            const token = generateFullToken();

            if (!token) {
                console.error('❌ Token 生成失敗');
                return Promise.reject(new Error('Token 生成失敗'));
            }

            // 添加 Token 到 header
            config.headers['Token'] = token;
        }

        return config;
    },
    error => {
        console.error('❌ 請求攔截器錯誤:', error);
        return Promise.reject(error);
    }
);

// 響應攔截器
service.interceptors.response.use(
    response => {
        const res = response.data;

        // 根據後端返回的 State 欄位判斷
        // '101' 表示成功
        if (res.State === '101') {
            return res;
        }

        // 其他狀態碼
        console.warn('⚠️ API 返回錯誤:', {
            state: res.State,
            message: res.Message,
            url: response.config.url
        });

        // 可以根據不同的 State 進行不同的處理
        // 例如: '401' 表示未授權，需要重新登錄
        if (res.State === '401' || res.State === 401) {
            console.error('❌ Token 驗證失敗，請重新登錄');
            logout();
            // window.location.href = '/login';
        }

        return res;
    },
    error => {
        console.error('❌ 響應錯誤:', error);

        // 處理 HTTP 錯誤
        if (error.response) {
            const { status, data } = error.response;

            switch (status) {
                case 401:
                    console.error('❌ 未授權，請重新登錄');
                    logout();
                    // window.location.href = '/login';
                    break;
                case 403:
                    console.error('❌ 禁止訪問');
                    break;
                case 404:
                    console.error('❌ 請求的資源不存在');
                    break;
                case 500:
                    console.error('❌ 伺服器錯誤');
                    break;
                default:
                    console.error(`❌ HTTP 錯誤 ${status}:`, data);
            }
        } else if (error.request) {
            console.error('❌ 網路錯誤，無法連接到伺服器');
        } else {
            console.error('❌ 請求配置錯誤:', error.message);
        }

        return Promise.reject(error);
    }
);

export default service;
